// Cálculos de Seguridad e Higiene sobre lo VERIFICADO (backend /api/seguridad/:feedId/verificado).
//
// El modelo de obra (YOLO / RT-DETR) solo elige qué fotos y qué cajas mirar; cada caja la revisa Claude:
// ¿es una persona?, ¿tiene casco y chaleco? (si / no / no_se_ve). Calzado y guantes no se miden: desde una
// cámara de obra no se ven con la claridad suficiente.
// Acá se cuenta SOLO lo confirmado con certeza alta, y cada elemento solo donde se veía:
//   cumplimiento = "si" / ("si" + "no"). Lo "no_se_ve" no suma ni resta: se informa aparte.
// Todo en hora de obra (Argentina, UTC-3).
import { puntoApoyo, puntoEnPoligono } from '../IADashboard/zonas';

export const ELEMENTOS = [
  { key: 'casco', label: 'Casco', falta: 'Sin casco' },
  { key: 'chaleco', label: 'Chaleco', falta: 'Sin chaleco' },
];
export const HORAS = Array.from({ length: 24 }, (_, h) => h);
const AR_OFF = 3 * 3600e3;
export const diaDe = (ms) => new Date(ms - AR_OFF).toISOString().slice(0, 10);
export const horaDe = (ms) => new Date(ms - AR_OFF).getUTCHours();

/** Persona confirmada: Claude dijo que es persona con certeza alta. */
export const confirmada = (p) => p.esPersona && p.certeza === 'alta';
const caja = (p) => ({ x1: p.box[0], y1: p.box[1], x2: p.box[2], y2: p.box[3] });

/** Zona de riesgo donde está parada la persona (la de nivel alto primero), o null. */
export function zonaDe(p, foto, zonas) {
  if (!zonas?.length) return null;
  const [x, y] = puntoApoyo(caja(p), foto.w, foto.h);
  let medio = null;
  for (const z of zonas) {
    if (!puntoEnPoligono(x, y, z.puntos)) continue;
    if (z.nivel !== 'medio') return z;
    medio = medio || z;
  }
  return medio;
}

const cuenta = () => Object.fromEntries(ELEMENTOS.map((e) => [e.key, { si: 0, no: 0, nv: 0 }]));
const sumar = (c, p) => { for (const e of ELEMENTOS) { const v = p[e.key]; if (v === 'si') c[e.key].si += 1; else if (v === 'no') c[e.key].no += 1; else c[e.key].nv += 1; } };
/** Personas mínimas (donde se veía el elemento) para mostrar un porcentaje: con menos, un % engaña. */
export const MIN_MUESTRA = 5;
export const pct = (x, min = 1) => (x && x.si + x.no >= min ? (100 * x.si) / (x.si + x.no) : null);

/** Elementos obligatorios en la obra (config.obligatorios; por defecto, todos). Lo no obligatorio no se muestra ni es falta. */
export const elementosDe = (obligatorios) => ELEMENTOS.filter((e) => !obligatorios || obligatorios[e.key] !== false);

/**
 * @param dias  [{ dia, fotos: [{ id, ms, s3, w, h, personas: [...] }] }] del backend
 * @param zonas zonas de riesgo [{ id, nombre, puntos, nivel }]
 * @param obligatorios { casco, chaleco } de la obra: solo lo obligatorio cuenta como falta
 */
export function analizar(dias, zonas = [], obligatorios) {
  const elementos = elementosDe(obligatorios);
  const fotos = dias.flatMap((d) => d.fotos).sort((a, b) => a.ms - b.ms);
  const total = cuenta();
  const porDia = new Map();
  const horas = HORAS.map(() => cuenta());
  const porZona = new Map(zonas.map((z) => [z.id, { zona: z, personas: 0, horas: 0, faltas: cuenta(), fotos: 0 }]));
  const eventos = [];
  let personas = 0, descartadas = 0, dudosas = 0, hp = 0, hpRiesgo = 0, pico = 0, picoFoto = null, fotosConFalta = 0;

  fotos.forEach((f, i) => {
    const next = fotos[i + 1];
    // Cada foto verificada representa el rato hasta la siguiente del mismo día (máx. 30 min).
    const dt = Math.min(next && diaDe(next.ms) === diaDe(f.ms) ? next.ms - f.ms : 20 * 60e3, 30 * 60e3) / 3600e3;
    const k = diaDe(f.ms), h = horaDe(f.ms);
    let d = porDia.get(k);
    if (!d) { d = { key: k, fotos: 0, personas: 0, pico: 0, hp: 0, hpRiesgo: 0, fotosConFalta: 0, c: cuenta(), primera: null, ultima: null }; porDia.set(k, d); }
    d.fotos += 1;
    const ok = f.personas.filter(confirmada);
    // El backend ya manda solo personas y cuántas se descartaron (descartadas); datos viejos traen todo.
    descartadas += f.descartadas ?? f.personas.filter((p) => !p.esPersona).length;
    dudosas += f.personas.filter((p) => p.esPersona && p.certeza !== 'alta').length;
    if (!ok.length) return;
    personas += ok.length; hp += ok.length * dt;
    d.personas += ok.length; d.hp += ok.length * dt; d.pico = Math.max(d.pico, ok.length);
    if (d.primera == null) d.primera = f.ms;
    d.ultima = f.ms;
    if (ok.length > pico) { pico = ok.length; picoFoto = f; }
    const zonasFoto = new Set();
    let falta = false;
    for (const p of ok) {
      sumar(total, p); sumar(d.c, p); sumar(horas[h], p);
      const faltan = elementos.filter((e) => p[e.key] === 'no').map((e) => e.key);
      if (faltan.length) falta = true;
      const z = zonaDe(p, f, zonas);
      if (z) {
        const zz = porZona.get(z.id);
        zz.personas += 1; zz.horas += dt; sumar(zz.faltas, p);
        zonasFoto.add(z.id);
        hpRiesgo += dt; d.hpRiesgo += dt;
      }
      if (faltan.length || z) eventos.push({ foto: f, p, faltan, zona: z });
    }
    zonasFoto.forEach((id) => { porZona.get(id).fotos += 1; });
    if (falta) { fotosConFalta += 1; d.fotosConFalta += 1; }
  });

  return {
    fotos: fotos.length,
    personas, descartadas, dudosas,
    horasPersona: hp, horasRiesgo: hpRiesgo,
    pico, picoFoto, fotosConFalta,
    total,
    dias: [...porDia.values()].sort((a, b) => (a.key < b.key ? -1 : 1)),
    horas,
    zonas: [...porZona.values()],
    eventos: eventos.sort((a, b) => b.foto.ms - a.foto.ms),
  };
}

/** Frases cortas para el jefe de obra / responsable de seguridad. */
export function recomendaciones(a, meta, { personasDia, obligatorios } = {}) {
  const out = [];
  if (!a || !a.personas) return out;
  const r = Math.round;
  const casco = !obligatorios || obligatorios.casco !== false;
  for (const e of elementosDe(obligatorios)) {
    const x = a.total[e.key], v = pct(x, MIN_MUESTRA);
    if (v == null) continue;
    if (e.key === 'casco') {
      if (v >= meta) out.push({ tono: 'ok', txt: `Casco: ${r(v)}% de las personas verificadas lo tenía (meta ${meta}%).` });
      else out.push({ tono: 'mal', txt: `Casco: ${r(v)}% de las personas verificadas lo tenía, ${r(meta - v)} puntos debajo de la meta de ${meta}%.` });
    } else if (v < meta && x.si + x.no >= 5) {
      out.push({ tono: 'mal', txt: `${e.label}: solo ${r(v)}% (${x.si} de ${x.si + x.no} personas donde se veía).` });
    }
  }
  const malas = a.horas.map((c, h) => ({ h, v: pct(c.casco), n: c.casco.si + c.casco.no })).filter((x) => x.n >= 4 && x.v != null).sort((x, y) => x.v - y.v);
  if (casco && malas.length >= 3 && malas[0].v < meta) {
    const h = String(malas[0].h).padStart(2, '0'), h1 = String(malas[0].h + 1).padStart(2, '0');
    out.push({ tono: 'mal', txt: `La peor franja para el casco es de ${h} a ${h1} h (${r(malas[0].v)}%). Conviene reforzar el control a esa hora.` });
  }
  const zonaPeor = [...a.zonas].filter((z) => z.personas).sort((x, y) => y.horas - x.horas)[0];
  if (zonaPeor) {
    const sin = casco ? zonaPeor.faltas.casco.no : 0;
    out.push({ tono: sin ? 'mal' : 'aviso', txt: `"${zonaPeor.zona.nombre}": gente cerca durante ${fmtHoras(zonaPeor.horas)}${sin ? `, ${sin} ${sin === 1 ? 'vez' : 'veces'} sin casco` : ''}.` });
  }
  if (casco && personasDia && personasDia.sinCascoSiempre > 0) out.push({ tono: 'mal', txt: `${personasDia.sinCascoSiempre} ${personasDia.sinCascoSiempre === 1 ? 'persona estuvo' : 'personas estuvieron'} sin casco todo el día ${personasDia.fecha}.` });
  if (a.descartadas) out.push({ tono: 'info', txt: `Se descartaron ${a.descartadas} ${a.descartadas === 1 ? 'detección que no era una persona' : 'detecciones que no eran personas'} (baldes, postes, sombras…). No cuentan.` });
  return out;
}

export const fmtHoras = (h) => {
  if (h == null) return '—';
  if (h < 1) return `${Math.max(1, Math.round(h * 60))} min`;
  return `${h.toLocaleString('es-AR', { maximumFractionDigits: h < 10 ? 1 : 0 })} h`;
};
export const fmtPermanencia = (min) => {
  if (min == null) return '—';
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60), m = min % 60;
  return m ? `${h} h ${m} min` : `${h} h`;
};
